import { useLatestSubmissionForSource } from '@commenda/api/form-builder/form-submissions';
import { useFormsTemplate } from '@commenda/api/form-builder/forms-templates';
import { invalidateWorkflowExecution } from '@commenda/api/workflow-execution/helpers';
import { FeatureLaunch, isFeatureLaunched } from '@commenda/feature-launches';
import {
  CountryCode,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Icons,
  LoadingIndicator,
  useToggle,
} from '@commenda/kakashi';
import { FormSubmissionSourceType } from '@commenda/prisma';
import type { DSLFile } from '@commenda/types';
import { TaskParty, TaskStatus } from '@prisma/client';
import { useMemo } from 'react';
import { WorkflowStepDescription } from '../../../components';
import { NewIncorporationLayout } from '../../../form-builder/components/new-incorp-layout';
import { Review } from '../../../form-builder/components/submissions/Review';
import { DSLFormRenderer } from '../../../form-builder/hooks/useFormRenderer';
import { useNotification } from '../../../hooks/useNotification';
import { useExportFormResponses } from '../../hook/useExportFormResponses';
import { ShareFormDialog } from '../../share-forms';
import { TCommonTaskProp } from '../../types';
import { updateTaskStatus } from '../../utils';

type HumanFormModalProps = {
  isOpen: boolean;
  onClose: () => void;
  formId: string;
  title: string;
  description?: string;
  task: TCommonTaskProp['task'];
  context: TCommonTaskProp['context'];
  assigneeType: TaskParty;
};

export const HumanFormModal = ({
  isOpen,
  onClose,
  formId,
  title,
  description,
  task,
  context,
  assigneeType,
}: HumanFormModalProps) => {
  const { setNotification } = useNotification();
  const { template, isLoading, error } = useFormsTemplate(formId);
  const corporation = context.serviceRequest?.corporation;
  const shareFormDialogVisibility = useToggle(false);
  const { exportPdf, isExporting } = useExportFormResponses({
    serviceRequestId: context.serviceRequest?.id,
    taskId: task.id,
    entityName: corporation?.companyLegalName,
    title: task.title,
  });
  const formTitle =
    (template?.dsl as unknown as DSLFile | undefined)?.form?.title ||
    template?.name ||
    title;
  const isFormSharingLaunched = isFeatureLaunched(
    FeatureLaunch.FormExternalShare,
    context.serviceRequest?.companyId ?? undefined
  );

  const directorId = useMemo(() => {
    const runtimeCtx = context.workflowExecution?.runtime?.context;
    return runtimeCtx?.directorId != null
      ? Number(runtimeCtx.directorId)
      : undefined;
  }, [context.workflowExecution]);

  const formSubmissionId = useMemo(() => {
    if (context.workflowExecution?.runtime?.stepExecutions) {
      const stepExecution = Object.values(
        context.workflowExecution.runtime.stepExecutions
      ).find(
        (execution) =>
          execution.stepDefinitionId === task.workflowDefinitionStepId
      );
      if (stepExecution?.outputData?.formSubmissionId) {
        return stepExecution.outputData.formSubmissionId as string;
      }
    }
    return null;
  }, [context.workflowExecution, task.workflowDefinitionStepId]);

  // When a task has been re-opened (e.g. by a workflow reset) it has no current
  // submission, so prefill the form from this task's latest prior submission.
  // Saving creates a new submission, so history is preserved.
  const shouldPrefillFromHistory =
    task.status !== TaskStatus.COMPLETED && !formSubmissionId;
  const { submission: priorSubmission, isLoading: isLoadingPriorSubmission } =
    useLatestSubmissionForSource(
      FormSubmissionSourceType.SERVICE_REQUEST_TASK,
      shouldPrefillFromHistory ? String(task.id) : null
    );

  const handleSubmitSuccess = async (submissionId: string) => {
    const result = await updateTaskStatus({
      task,
      mutate: context.mutate,
      status: TaskStatus.COMPLETED,
      metadata: {
        formSubmissionId: submissionId,
      },
    });

    if (!result) {
      setNotification({
        title: 'Error',
        message: 'Failed to update task with form submission',
        valid: false,
        show: true,
      });
      return;
    }

    if (context.workflowExecution?.id) {
      invalidateWorkflowExecution(context.workflowExecution.id);
    }

    setNotification({
      title: 'Success',
      message: 'Form submitted successfully',
      valid: true,
      show: true,
    });
    onClose();
  };

  const handleDraftSaved = async (submissionId: string) => {
    await updateTaskStatus({
      task,
      mutate: context.mutate,
      status: TaskStatus.IN_PROGRESS,
      metadata: {
        formSubmissionId: submissionId,
      },
    });
  };

  const handleSaveSuccess = async (submissionId: string) => {
    const result = await updateTaskStatus({
      task,
      mutate: context.mutate,
      status: TaskStatus.IN_PROGRESS,
      metadata: {
        formSubmissionId: submissionId,
      },
    });

    if (!result) {
      setNotification({
        title: 'Error',
        message: 'Failed to update task status',
        valid: false,
        show: true,
      });
      return;
    }

    setNotification({
      title: 'Success',
      message: 'Form saved as draft',
      valid: true,
      show: true,
    });
    onClose();
  };

  if (isLoading || (shouldPrefillFromHistory && isLoadingPriorSubmission)) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <WorkflowStepDescription description={description} />
          </DialogHeader>
          <div className="flex items-center justify-center py-12">
            <LoadingIndicator />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (error || !template) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <WorkflowStepDescription description={description} />
          </DialogHeader>
          <div className="text-center py-12">
            <Icons.AlertCircle className="w-12 h-12 text-text-error mx-auto mb-4" />
            <p className="txt-medium-medium text-text-primary mb-2">
              Failed to load form
            </p>
            <p className="txt-small-regular text-text-tertiary">
              {error
                ? 'An error occurred while loading the form. Please try again.'
                : 'The form template could not be found.'}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!template.key) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <WorkflowStepDescription description={description} />
          </DialogHeader>
          <div className="text-center py-12">
            <Icons.AlertCircle className="w-12 h-12 text-text-error mx-auto mb-4" />
            <p className="txt-medium-medium text-text-primary mb-2">
              Form Configuration Error
            </p>
            <p className="txt-small-regular text-text-tertiary">
              The form template is missing a key and cannot accept submissions.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // For incomplete tasks, show editable form
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        hideCloseIcon
        className="max-w-[100vw] h-[100vh] max-h-[100vh]"
      >
        {/* 
        
        DON'T REMOVE THIS COMMENTED CODE : THIS IS BREAKS FOR CLIENTS{(title || description) ? (
          <div className="px-6 pt-6 pb-2">
            {title ? (
              <h2 className="txt-medium-bold text-text-primary">{title}</h2>
            ) : null}
            <WorkflowStepDescription description={description} />
          </div>
        ) : null} */}
        {task.status === TaskStatus.COMPLETED ? (
          <NewIncorporationLayout
            steps={[]}
            activeStepIndex={0}
            onStepClick={() => {}}
            title={title}
            subtitle={description}
            hideSidebar
            hideActions
            header={{
              entity: corporation
                ? {
                    name: corporation.companyLegalName,
                    // incorporationCountry is an ISO-3166 alpha-2 string, same
                    // cast the editable form path makes for this header.
                    countryCode:
                      corporation.incorporationCountry as CountryCode,
                  }
                : undefined,
              onDownload: exportPdf,
              isDownloading: isExporting,
              onClose,
            }}
          >
            <Review
              submissionId={formSubmissionId}
              corporationId={
                context.serviceRequest?.corporationId ||
                context.serviceRequest?.companyId
              }
              showCopyButton={assigneeType !== TaskParty.CLIENT}
              pageTitle={title}
              className="w-full"
            />
          </NewIncorporationLayout>
        ) : (
          <DSLFormRenderer
            dsl={template.dsl as unknown as DSLFile}
            formTemplateId={template.id}
            formTemplateKey={template.key}
            existingSubmissionId={formSubmissionId}
            onSubmitSuccess={handleSubmitSuccess}
            onSaveSuccess={handleSaveSuccess}
            onDraftSaved={handleDraftSaved}
            onClose={onClose}
            corporationId={context.serviceRequest?.corporationId}
            companyId={context.serviceRequest?.companyId}
            directorId={directorId}
            serviceRequestId={context.serviceRequest?.id}
            serviceRequestTaskId={task.id}
            prefillFormData={priorSubmission?.formResponse}
            dslContext={{
              companyId: context.serviceRequest?.companyId,
              corporationId: context.serviceRequest?.corporationId,
              ...(context.workflowExecution?.runtime?.context as Record<
                string,
                unknown
              >),
            }}
            header={{
              entityName: context.serviceRequest?.corporation?.companyLegalName,
              countryCode:
                context.serviceRequest?.corporation?.incorporationCountry,
              onShare: isFormSharingLaunched
                ? shareFormDialogVisibility.on
                : undefined,
            }}
          />
        )}

        <ShareFormDialog
          isOpen={isFormSharingLaunched && shareFormDialogVisibility.value}
          onClose={shareFormDialogVisibility.off}
          serviceRequestId={context.serviceRequest?.id ?? null}
          taskId={task.id}
          defaultName={formTitle}
        />
      </DialogContent>
    </Dialog>
  );
};
